import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { api } from "@jourfx/convex/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from "@jourfx/ui";

import { PageHeader } from "../../components/PageHeader";
import { useDateFormat } from "@/hooks/useDateFormat";
import { useSession } from "../../lib/auth";

type Id<T extends string> = string & { __tableName: T };

type OrganizationOption = {
  _id: string;
  name: string;
};

type VersicherteListItem = {
  _id: string;
  organizationId: string;
  organizationName?: string | null;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  accessCode?: string;
  createdAt: number;
};

export function VersicherteList() {
  const { t } = useTranslation("versicherte");
  const { formatShortDate } = useDateFormat();
  const { toast } = useToast();
  const session = useSession();
  const canSelectOrganization = session.isAdministrator || session.isSuperAdmin;
  const apiRef = api as any;

  const [search, setSearch] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createdProfile, setCreatedProfile] = useState<{
    accessCode: string;
    versichertenProfileId: string;
  } | null>(null);

  const organizations = useQuery(
    apiRef.admin.organizations.list,
    canSelectOrganization ? { status: "active", limit: 100 } : "skip"
  ) as { organizations: OrganizationOption[] } | undefined;

  const selectedOrgId = organizationId || undefined;

  const versicherte = useQuery(apiRef.admin.versicherte.list, {
    organizationId: selectedOrgId as any,
    search: search.trim() || undefined,
    limit: 200,
  }) as VersicherteListItem[] | undefined;

  const createVersichertenprofil = useMutation(apiRef.admin.versicherte.create);

  const handleCreate = async () => {
    if (!firstName.trim() || !lastName.trim()) return;

    if (!selectedOrgId) {
      toast({
        title: t("newProfile.selectOrganizationTitle"),
        description: t("newProfile.selectOrganizationDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const result = await createVersichertenprofil({
        organizationId: selectedOrgId as any,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });

      setCreatedProfile({
        accessCode: result.accessCode,
        versichertenProfileId: result.versichertenProfileId,
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
    } catch (error) {
      toast({
        title: t("newProfile.errorTitle"),
        description: error instanceof Error ? error.message : t("newProfile.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t("title")} description={t("subtitle")} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("newProfile.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {canSelectOrganization ? (
            <div className="space-y-2">
              <Label htmlFor="versicherte-organization">{t("newProfile.organization")}</Label>
              <select
                id="versicherte-organization"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
              >
                <option value="">{t("newProfile.selectOrganization")}</option>
                {organizations?.organizations?.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="versicherte-first-name">{t("newProfile.firstName")}</Label>
              <Input
                id="versicherte-first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-last-name">{t("newProfile.lastName")}</Label>
              <Input
                id="versicherte-last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-email">{t("newProfile.email")}</Label>
              <Input
                id="versicherte-email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-phone">{t("newProfile.phone")}</Label>
              <Input
                id="versicherte-phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleCreate}
            disabled={isCreating || !firstName.trim() || !lastName.trim()}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("newProfile.create")}
          </Button>
        </CardContent>
      </Card>

      {createdProfile ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <h3 className="mb-2 font-semibold text-green-800">
            {t("credentials.created")}
          </h3>
          <div className="space-y-2">
            <div className="rounded-md border border-green-200 p-3">
              <span className="text-sm text-green-700">{t("credentials.accessCode")}</span>
              <div className="mt-1 font-mono text-sm font-bold">
                {createdProfile.accessCode}
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-green-600">{t("credentials.hint")}</p>
          <div className="mt-3 flex gap-3">
            <Link
              to="/versicherte/$versicherteId"
              params={{
                versicherteId:
                  createdProfile.versichertenProfileId as Id<"versichertenProfiles">,
              }}
              className="text-sm font-medium text-green-800 hover:underline"
            >
              {t("credentials.openProfile")}
            </Link>
            <button
              type="button"
              className="text-sm text-green-700 hover:text-green-900"
              onClick={() => setCreatedProfile(null)}
            >
              {t("credentials.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("list.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("list.search")}
              className="pl-9"
            />
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  {canSelectOrganization ? (
                    <th className="px-3 py-2 font-medium">{t("list.columns.organization")}</th>
                  ) : null}
                  <th className="px-3 py-2 font-medium">{t("list.columns.name")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.email")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.phone")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.created")}</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {versicherte?.map((versicherter) => (
                  <tr key={versicherter._id} className="border-t">
                    {canSelectOrganization ? (
                      <td className="px-3 py-2">{versicherter.organizationName || "-"}</td>
                    ) : null}
                    <td className="px-3 py-2">
                      {versicherter.firstName} {versicherter.lastName}
                    </td>
                    <td className="px-3 py-2">{versicherter.email || "-"}</td>
                    <td className="px-3 py-2">{versicherter.phone || "-"}</td>
                    <td className="px-3 py-2">{formatShortDate(versicherter.createdAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to="/versicherte/$versicherteId"
                        params={{
                          versicherteId:
                            versicherter._id as Id<"versichertenProfiles">,
                        }}
                        className="text-primary hover:underline"
                      >
                        {t("list.details")}
                      </Link>
                    </td>
                  </tr>
                ))}
                {versicherte?.length === 0 ? (
                  <tr>
                    <td
                      className="px-3 py-6 text-muted-foreground"
                      colSpan={canSelectOrganization ? 6 : 5}
                    >
                      {t("list.empty")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
